import React from 'react';
import clsx from 'clsx';
import { mergeTailwindClasses } from '@kontentino/ui';

/** Single source of truth for table header label styling. */
export const tableHeaderTextClass = 'tw-font-semibold tw-text-grayscale-180';

export const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={mergeTailwindClasses(
      clsx('tw-w-full tw-caption-bottom tw-text-sm', className),
    )}
    {...props}
  />
));
Table.displayName = 'Table';

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={mergeTailwindClasses(
      clsx('[&_tr]:tw-border-b [&_tr]:tw-border-grayscale-20', className),
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={mergeTailwindClasses(
      clsx('[&_tr:last-child]:tw-border-0', className),
    )}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

export const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={mergeTailwindClasses(
      clsx(
        'tw-border-t tw-border-grayscale-20 tw-bg-grayscale-5 [&_tr]:tw-border-0',
        className,
      ),
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={mergeTailwindClasses(
      clsx(
        'tw-group tw-border-b tw-border-grayscale-20 tw-transition-colors hover:tw-bg-grayscale-10',
        className,
      ),
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={mergeTailwindClasses(
      clsx(
        'tw-px-3 tw-py-3 tw-text-left tw-align-middle',
        tableHeaderTextClass,
        className,
      ),
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={mergeTailwindClasses(
      clsx('tw-px-3 tw-py-4 tw-align-middle', className),
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';
